/**
 * LiveKit transport wiring.
 *
 * Models reconnection FROM THE START: LiveKit RoomEvents are mapped to the droom
 * ConnectionState machine so the UI can render the calm resume-first overlay
 * instead of dropping the call (UX_SPEC §3.6).
 *
 * The room token AND the wss URL are ALWAYS minted/served server-side and
 * fetched per session — the client never hardcodes `wss://livekit...`, so one
 * bundle works across environments (the backend returns the right `url`).
 */

import { Room, RoomEvent, DisconnectReason, type RoomOptions } from 'livekit-client';
import type { ConnectionState } from '@/types';
import { API_BASE_URL, isMockMode, request } from './client';

export interface LiveKitGrant {
  /** wss signalling URL for the self-hosted LiveKit — supplied by the backend. */
  url: string;
  /** Short-lived, server-minted room access token (publish + subscribe grants). */
  token: string;
  /** The LiveKit room name (server-derived from the session). */
  roomName: string;
  /** This participant's identity within the room. */
  identity: string;
  /** Which side we joined as — always CUSTOMER from the droom. */
  role: 'AGENT' | 'CUSTOMER';
}

/**
 * Fetch a per-session join grant from the backend. The droom always joins as
 * CUSTOMER. Connect with the returned `grant.url` — never a hardcoded host.
 * A 400/403 (session not READY/LIVE, or no media scope) throws ApiError.
 */
export async function fetchLiveKitToken(sessionId: string): Promise<LiveKitGrant> {
  return request<LiveKitGrant>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/live/join?role=CUSTOMER`,
    { method: 'POST' },
  );
}

export interface LiveKitSession {
  room: Room;
  connect: (grant: LiveKitGrant) => Promise<void>;
  disconnect: () => void;
}

/**
 * A control message relayed into the room BY THE SERVER (owner round 2026-08-24, item 4).
 *
 * <p>The only kind that exists today is the officer-triggered shutter. It carries no authority and no
 * customer data — it names the prompt whose shutter to fire, and this client re-validates every local
 * precondition (purpose acknowledged, camera live, no shot in preview, attempts left) before it acts.
 * The message is a NUDGE that collapses poll latency to nothing; the server row is the record.
 *
 * <p>Unknown `t` values are ignored, so a backend that grows a second message kind cannot break a
 * droom that predates it.
 */
export interface CaptureShutterSignal {
  readonly t: 'vcip.capture.shutter.v1';
  readonly promptId: string;
  readonly invocationId?: string;
}

/**
 * Parse a room data payload into a known signal, or null.
 *
 * <p>Deliberately total and never throwing: this runs on bytes that arrived over the network, and a
 * malformed packet must be a no-op rather than an exception that unmounts the live call.
 */
export function parseRoomSignal(payload: Uint8Array): CaptureShutterSignal | null {
  try {
    const text = new TextDecoder().decode(payload);
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const msg = parsed as Record<string, unknown>;
    if (msg.t !== 'vcip.capture.shutter.v1') return null;
    if (typeof msg.promptId !== 'string' || msg.promptId === '') return null;
    return {
      t: 'vcip.capture.shutter.v1',
      promptId: msg.promptId,
      ...(typeof msg.invocationId === 'string' ? { invocationId: msg.invocationId } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Subscribe to server-relayed room signals. Returns the unsubscribe.
 *
 * <p>LiveKit delivers `DataReceived` with the sending participant when a PEER sent it, and with
 * `undefined` when the SERVER did. We accept only the server-sent case: the shutter is an audited
 * officer act relayed by the backend, so a packet that arrives from a participant — even a legitimate
 * one — is not the channel this feature uses, and honouring it would create exactly the unaudited
 * peer-to-peer path the design refuses.
 */
export function onRoomSignal(
  room: Room,
  handler: (signal: CaptureShutterSignal) => void,
): () => void {
  const listener = (payload: Uint8Array, participant?: unknown): void => {
    if (participant !== undefined) return; // peer-sent — not the audited server relay
    const signal = parseRoomSignal(payload);
    if (signal) handler(signal);
  };
  room.on(RoomEvent.DataReceived, listener);
  return () => {
    room.off(RoomEvent.DataReceived, listener);
  };
}

/**
 * Create a Room and translate its lifecycle into ConnectionState callbacks.
 * Mapping (the resume model):
 *   Reconnecting                → 'reconnecting'  (calm overlay, lock held)
 *   Reconnected                 → 'resumed' → 'connected'
 *   Disconnected (recoverable)  → 'suspended'     (offer a resume link)
 *   Disconnected (server ended) → 'ended'         (the officer finished — normal)
 *   Disconnected (terminal)     → 'expired'
 */
export function createLiveKitSession(
  onState: (state: ConnectionState) => void,
  roomOptions?: RoomOptions,
): LiveKitSession {
  const room = new Room(roomOptions);

  room
    .on(RoomEvent.Reconnecting, () => onState('reconnecting'))
    .on(RoomEvent.Reconnected, () => {
      onState('resumed');
      onState('connected');
    })
    .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      // A customer-initiated hang-up is a clean end; anything else is a drop we
      // treat as resumable (SUSPENDED, not CLOSED) unless the server ended it.
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        onState('idle');
      } else if (
        // The officer finished and the backend tore the room down (endRoom → deleteRoom).
        // Depending on server version the client sees the room going away or itself being
        // removed from it; both mean the same thing to the customer — the call is OVER, and
        // normally so. Mapping these to 'expired' (as ROOM_DELETED used to be) told a customer
        // whose verification had just gone perfectly that their session had lapsed.
        reason === DisconnectReason.ROOM_DELETED ||
        reason === DisconnectReason.PARTICIPANT_REMOVED
      ) {
        onState('ended');
      } else if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        // The same session opened elsewhere — this device is the stale one. The journey is ALIVE in
        // the customer's other hand, so this is 'superseded', not 'expired' (2026-08-24): telling
        // someone mid-journey that their session "has ended / the link may have expired" is a false
        // alarm that sends them back to support.
        onState('superseded');
      } else {
        // Network faults and SERVER_SHUTDOWN: a drop worth resuming, never an ending.
        onState('suspended');
      }
    });

  return {
    room,
    connect: async (grant: LiveKitGrant) => {
      if (!grant.url || !grant.token) return; // nothing to connect to
      onState('connecting');
      // Connect to the URL RETURNED BY THE API (never a hardcoded host).
      await room.connect(grant.url, grant.token);
      onState('connected');
    },
    disconnect: () => {
      void room.disconnect();
    },
  };
}

/**
 * True when a real backend is reachable to mint the join grant. The wss URL now
 * comes from that grant, so it is no longer part of this gate (was: also
 * required a VITE_LIVEKIT_URL). In mock mode this is false and LiveCall renders
 * the illustrative demo scene instead of a live transport.
 */
export const livekitConfigured = (): boolean => !isMockMode() && !!API_BASE_URL;
